import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Percent, Calculator, RefreshCw, AlertTriangle, 
  CheckCircle, Trophy, Info 
} from 'lucide-react';
import { updatePayoutStructure } from '../../services/api';

const PayoutTab = ({
  users = [],
  totalUsers = 0,
  setTotalUsers,
  financialSettings = { cost_per_race: 1.0 },
  payoutStructure,
  setPayoutStructure,
  payoutLoading = false,
  loadPayoutStructure,
  payoutSaving = false,
  setPayoutSaving,
  complianceCheck,
  checkingCompliance = false,
  runComplianceCheck,
  toast
}) => {
  // Local state
  const [editingPayoutCategory, setEditingPayoutCategory] = useState(null);

  // Load payout structure when component mounts
  useEffect(() => {
    if (loadPayoutStructure) loadPayoutStructure();
    const numParticipants = totalUsers || users.length || 50;
    if (setTotalUsers) setTotalUsers(numParticipants);
    if (runComplianceCheck) runComplianceCheck(numParticipants, financialSettings.cost_per_race);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update payout percentage
  const handlePayoutPercentageChange = (category, position, newPercentage) => {
    if (!setPayoutStructure) return;
    setPayoutStructure(prev => {
      const updated = { ...prev };
      const payouts = [...updated[category].payouts];
      const idx = payouts.findIndex(p => p.position === position);
      if (idx !== -1) {
        payouts[idx] = { ...payouts[idx], percentage: parseFloat(newPercentage) || 0 };
      }
      updated[category] = { ...updated[category], payouts };
      return updated;
    });
  };

  // Calculate total percentage for a category
  const getCategoryTotal = (category) => {
    if (!payoutStructure || !payoutStructure[category]) return 0;
    return payoutStructure[category].payouts.reduce((sum, p) => sum + (p.percentage || 0), 0);
  };

  // Save payout structure
  const handleSavePayoutStructure = async () => {
    if (setPayoutSaving) setPayoutSaving(true);
    try {
      await updatePayoutStructure(payoutStructure);
      if (toast) toast({
        title: 'Opgeslagen',
        description: 'Uitbetalingsstructuur is bijgewerkt',
      });
      setEditingPayoutCategory(null);
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon structuur niet opslaan',
        variant: 'destructive',
      });
    } finally {
      if (setPayoutSaving) setPayoutSaving(false);
    }
  };

  // Accept proposed compliant structure
  const handleAcceptProposedStructure = async () => {
    if (!complianceCheck?.proposed_structure) return;
    
    if (setPayoutSaving) setPayoutSaving(true);
    try {
      const currentStructure = payoutStructure || {};
      const category = complianceCheck.category;
      
      const updatedStructure = {
        ...currentStructure,
        [category]: complianceCheck.proposed_structure
      };
      
      await updatePayoutStructure(updatedStructure);
      if (setPayoutStructure) setPayoutStructure(updatedStructure);
      
      if (toast) toast({
        title: 'Structuur Aangepast',
        description: `Uitbetalingspercentages voor ${category} deelnemers zijn aangepast om binnen €449 te blijven.`,
      });
      
      if (runComplianceCheck) await runComplianceCheck();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon structuur niet opslaan',
        variant: 'destructive',
      });
    } finally {
      if (setPayoutSaving) setPayoutSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Percent className="mr-2 text-[#FDB913]" size={20} />
          Uitbetalingsstructuur Beheer
        </CardTitle>
        <CardDescription>
          Pas de uitbetalingspercentages per positie aan voor elke deelnemers-categorie. 
          Percentages moeten optellen tot 100%.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Simulation Input */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg">
          <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-3 flex items-center">
            <Calculator className="mr-2" size={18} />
            Compliance Simulatie
          </h4>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-sm text-blue-600 dark:text-blue-400">Aantal Deelnemers</Label>
              <Input
                type="number"
                min="1"
                max="2000"
                value={totalUsers || users.length || 20}
                onChange={(e) => setTotalUsers(parseInt(e.target.value) || 0)}
                className="w-32 mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-blue-600 dark:text-blue-400">Kosten per race (€)</Label>
              <div className="w-32 mt-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-input rounded-md text-sm text-foreground">
                {financialSettings.cost_per_race}
              </div>
            </div>
            <div>
              <Label className="text-sm text-blue-600 dark:text-blue-400">Prijzenpot</Label>
              <div className="mt-1 px-3 py-2 bg-white dark:bg-gray-800 border rounded-md font-bold text-lg">
                €{((totalUsers || users.length || 20) * financialSettings.cost_per_race * 24).toFixed(2)}
              </div>
            </div>
            <Button
              onClick={() => runComplianceCheck(totalUsers || users.length, financialSettings.cost_per_race)}
              disabled={checkingCompliance}
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              {checkingCompliance ? (
                <RefreshCw className="mr-2 animate-spin" size={16} />
              ) : (
                <Calculator className="mr-2" size={16} />
              )}
              Check Compliance
            </Button>
          </div>
        </div>

        {/* Compliance Warning Banner */}
        {complianceCheck && !complianceCheck.compliant && (
          <div className="mb-6 p-4 bg-orange-100 dark:bg-orange-900/30 border-2 border-orange-500 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" size={28} />
              <div className="flex-1">
                <h4 className="font-bold text-orange-700 dark:text-orange-400 text-lg">
                  Gedoogbeleid Kansspelen - Actie Vereist
                </h4>
                <p className="text-sm text-orange-600 dark:text-orange-300 mt-1">
                  Met <strong>{totalUsers || users.length}</strong> deelnemers en €{financialSettings.cost_per_race}/race 
                  is de prijzenpot <strong>€{complianceCheck.prize_pool?.toFixed(2)}</strong>. 
                  {complianceCheck.violations?.[0] && (
                    <>
                      De huidige 1e prijs ({complianceCheck.violations[0].percentage}%) zou 
                      <strong> €{complianceCheck.violations[0].amount?.toFixed(2)}</strong> zijn, 
                      wat de €449 limiet overschrijdt.
                    </>
                  )}
                </p>
                
                <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-orange-300 dark:border-orange-700">
                  <h5 className="font-semibold text-orange-700 dark:text-orange-400 mb-2">Voorgestelde Aanpassing:</h5>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Categorie:</span>
                      <span className="ml-2 font-medium">{complianceCheck.category} deelnemers</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Prijswinnaars:</span>
                      <span className="ml-2 font-medium">{complianceCheck.proposed_winners}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Nieuwe 1e prijs:</span>
                      <span className="ml-2 font-medium text-green-600 dark:text-green-400">
                        {complianceCheck.proposed_structure?.payouts?.[0]?.percentage}% = €{((complianceCheck.proposed_structure?.payouts?.[0]?.percentage / 100) * complianceCheck.prize_pool).toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Laatste prijs:</span>
                      <span className="ml-2 font-medium">
                        €{complianceCheck.proposed_last_amount?.toFixed(2)} (min €{complianceCheck.min_payout})
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={handleAcceptProposedStructure}
                      disabled={payoutSaving}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      {payoutSaving ? (
                        <RefreshCw className="mr-2 animate-spin" size={16} />
                      ) : (
                        <CheckCircle className="mr-2" size={16} />
                      )}
                      Voorstel Accepteren & Opslaan
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compliance OK Banner */}
        {complianceCheck && complianceCheck.compliant && (
          <div className="mb-6 p-3 bg-green-100 dark:bg-green-900/30 border border-green-500 rounded-lg flex items-center">
            <CheckCircle className="text-green-600 dark:text-green-400 mr-3" size={24} />
            <div>
              <span className="font-medium text-green-700 dark:text-green-400">Gedoogbeleid OK</span>
              <span className="text-sm text-green-600 dark:text-green-300 ml-2">
                Bij {totalUsers || users.length} deelnemers blijven alle prijzen onder €449 en boven €{complianceCheck.min_payout} (mincash)
              </span>
            </div>
          </div>
        )}

        {payoutLoading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="animate-spin text-[#FDB913]" size={32} />
          </div>
        ) : payoutStructure ? (
          <div className="space-y-4">
            {/* Category Selection */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {Object.keys(payoutStructure).map((category) => (
                <button
                  key={category}
                  onClick={() => setEditingPayoutCategory(editingPayoutCategory === category ? null : category)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    editingPayoutCategory === category
                      ? 'bg-[#FDB913] text-[#1a2332]'
                      : 'bg-secondary hover:bg-secondary/80 text-foreground'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Edit Panel for Selected Category */}
            {editingPayoutCategory && payoutStructure[editingPayoutCategory] && (
              <Card className="border-2 border-[#FDB913] mt-4">
                <CardHeader className="bg-[#1a2332] text-white">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Trophy className="mr-2 text-[#FDB913]" size={20} />
                      {editingPayoutCategory} Deelnemers - {payoutStructure[editingPayoutCategory].winners} Prijswinnaars
                    </span>
                    <span className={`text-sm font-normal px-3 py-1 rounded ${
                      Math.abs(getCategoryTotal(editingPayoutCategory) - 100) <= 1
                        ? 'bg-green-600'
                        : 'bg-red-600'
                    }`}>
                      Totaal: {getCategoryTotal(editingPayoutCategory).toFixed(1)}%
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {payoutStructure[editingPayoutCategory].payouts.map((payout) => (
                      <div key={payout.position} className="flex flex-col">
                        <label className="text-sm font-medium text-muted-foreground mb-1">
                          {payout.position === 1 ? '1e' : payout.position === 2 ? '2e' : payout.position === 3 ? '3e' : `${payout.position}e`} Plaats
                        </label>
                        <div className="flex items-center">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={payout.percentage}
                            onChange={(e) => handlePayoutPercentageChange(editingPayoutCategory, payout.position, e.target.value)}
                            className="text-center"
                          />
                          <span className="ml-1 text-muted-foreground">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {Math.abs(getCategoryTotal(editingPayoutCategory) - 100) > 1 && (
                    <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 rounded-lg flex items-center text-red-700 dark:text-red-400">
                      <AlertTriangle className="mr-2" size={20} />
                      De percentages moeten optellen tot 100%. Huidig totaal: {getCategoryTotal(editingPayoutCategory).toFixed(1)}%
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Overview Table */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Info className="mr-2 text-blue-500" size={18} />
                  Overzicht Alle Categorieën
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#1a2332] text-white">
                      <tr>
                        <th className="p-3 text-left">Deelnemers</th>
                        <th className="p-3 text-center">Prijswinnaars</th>
                        <th className="p-3 text-right">1e Plaats</th>
                        <th className="p-3 text-right">2e Plaats</th>
                        <th className="p-3 text-right">3e Plaats</th>
                        <th className="p-3 text-center">Totaal %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {Object.entries(payoutStructure).map(([range, data]) => {
                        const total = data.payouts.reduce((sum, p) => sum + (p.percentage || 0), 0);
                        const isValid = Math.abs(total - 100) <= 1;
                        return (
                          <tr 
                            key={range} 
                            className={`hover:bg-secondary cursor-pointer ${editingPayoutCategory === range ? 'bg-[#FDB913]/20' : ''}`}
                            onClick={() => setEditingPayoutCategory(range)}
                          >
                            <td className="p-3 font-medium">{range}</td>
                            <td className="p-3 text-center text-[#FDB913] font-bold">{data.winners}</td>
                            <td className="p-3 text-right">{data.payouts[0]?.percentage || 0}%</td>
                            <td className="p-3 text-right">{data.payouts[1]?.percentage || '-'}%</td>
                            <td className="p-3 text-right">{data.payouts[2]?.percentage || '-'}%</td>
                            <td className={`p-3 text-center font-medium ${isValid ? 'text-green-600' : 'text-red-600'}`}>
                              {total.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end mt-6">
              <Button 
                onClick={handleSavePayoutStructure}
                disabled={payoutSaving}
                className="bg-[#FDB913] hover:bg-[#FDB913]/90 text-[#1a2332]"
              >
                {payoutSaving ? (
                  <RefreshCw className="mr-2 animate-spin" size={16} />
                ) : (
                  <CheckCircle className="mr-2" size={16} />
                )}
                {payoutSaving ? 'Opslaan...' : 'Uitbetalingsstructuur Opslaan'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Geen uitbetalingsstructuur gevonden
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PayoutTab;
